import React from 'react'
import moment from 'moment'
import { Formik, Form } from 'formik'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { withRouter } from 'react-router-dom'
import * as Yup from 'yup'

import { PRIORITY_TERM, URL_CAMPAIGN_TERMS } from '../../constants'
import Button from '../buttons/buttons.component'
import { AsyncSelectInput, DatepickerInput } from '../form-components'
import { TicketSaleService } from './../../services'
import { getApiErrorMessage } from './../../services/axios.service'
import { toastActions } from '../../actions'
import {
	iFrameHelper,
	markUpgradableTicket,
	isMobile,
	sortTerminalsByPriority,
	dateHelper,
	checkURLTerm,
	debounce,
	isValidLocalityId
} from './../../helpers'
import { Creators as SiteCreators } from '../../ducks/site/site'
import { Creators as ProgressBarCreators } from '../../ducks/progress-bar/progress-bar'
import { Creators as TicketSaleCreators } from '../../ducks/ticket-sale/ticket-sale'
import { conexaoActions } from '../../ducks/conexao/conexaoReducer'

import {
	InputSearchCitiesContainer,
	InputSearchDateContainer,
	StyledIconFontAwesome,
	SearchContainer,
	InputSearchCity,
	InputSearchDate,
	BtnSearchTrip,
	OriginOptionLabel
} from './search-form.styles'

import { routes } from '../../constants/routes.constants'

const ALLOWED_RANGE_IN_MONTHS = 24
const DEBOUNCE_FIND_ORIGINS = 'findOrigins'
const DEBOUNCE_FIND_DESTINATIONS = 'findDestinations'

const LOCALITY_KIND = {
	CITY: 0,
	TERMINAL: 1
}

const mapLocalityOption = locality => {
	const city = locality.displayName.replace(
		new RegExp(`,\\s*${locality.stateCode}$`),
		''
	)

	return {
		value: JSON.stringify({
			id: locality.id
		}),
		id: locality.id,
		label: locality.displayName,
		city,
		kind: locality.kind,
		hasChildren: !!(locality.children && locality.children.length),
		state: locality.stateCode,
		ibgeCode: locality.ibgeCode
	}
}

// "PORTO ALEGRE - RS" -> { city: "PORTO ALEGRE", state: "RS" } - mesmo separador que o legado usa
// pra combinar cidade+UF num campo só (ver TravelDisplayLabel.ExtractState no MuviHub). Também
// aceita "PORTO ALEGRE (RS)" (Bug 8003: formato usado pelos links estáticos do Muvibus - ver
// destinations.page.jsx) para não depender de um único separador vindo de todos os links legados.
const CITY_STATE_PARENS_PATTERN = /^(.*)\(([A-Z]{2})\)\s*$/i

const splitCityState = cityAndState => {
	const value = cityAndState || ''
	const idx = value.lastIndexOf(' - ')

	if (idx > 0) {
		return { city: value.slice(0, idx).trim(), state: value.slice(idx + 3).trim() }
	}

	const parensMatch = value.match(CITY_STATE_PARENS_PATTERN)

	if (parensMatch) {
		return { city: parensMatch[1].trim(), state: parensMatch[2].trim() }
	}

	return { city: value.trim(), state: '' }
}

// "PORTO ALEGRE" -> "Porto Alegre" - só pra exibição (o legado manda a cidade em CAIXA ALTA).
const titleCase = text =>
	(text || '').toLowerCase().replace(/(^|\s)\S/g, char => char.toUpperCase())

const flattenLocalities = localities =>
	localities.reduce((list, locality) => {
		list.push(mapLocalityOption(locality))

		if (locality.children && locality.children.length) {
			locality.children.forEach(child => list.push(mapLocalityOption(child)))
		}

		return list
	}, [])

const searchFormSchema = Yup.object().shape({
	tripDateGoingSearch: Yup.date(),
	tripDateReturnSearch: Yup.string().nullable(),
	origin: Yup.string('Informe o origem').required('Informe a origem'),
	destination: Yup.string('Informe o destino').required('Informe o destino')
})

class SearchFormComponent extends React.Component {
	constructor(props) {
		super(props)

		this.state = {
			formValues: {
				origin: props.origin ? props.origin.value : '',
				destination: props.destination ? props.destination.value : '',
				tripDateGoingSearch: props.tripDateGoingSearch
					? moment(props.tripDateGoingSearch).format('YYYY-MM-DD')
					: moment(dateHelper.getTomorrowDate()).format('YYYY-MM-DD'),
				tripDateReturnSearch: props.tripDateReturnSearch
					? moment(props.tripDateReturnSearch).format('YYYY-MM-DD')
					: ''
			},
			presetOrigin: null,
			presetDestination: null
		}
		this.getPlaces = this.getPlaces.bind(this)
		this.setSearchFormField = this.setSearchFormField.bind(this)
		this.getOriginList = this.getOriginList.bind(this)
		this.getDestinationList = this.getDestinationList.bind(this)
		this.alterOriginField = this.alterOriginField.bind(this)
		this.alterDestinationField = this.alterDestinationField.bind(this)
		this.renderFormik = this.renderFormik.bind(this)
		this.findOrigins = this.findOrigins.bind(this)
		this.findDestinations = this.findDestinations.bind(this)
		this.updateDestinations = this.updateDestinations.bind(this)
		this.setDestination = this.setDestination.bind(this)
		this.validateLocalities = this.validateLocalities.bind(this)
		this.searchTrips = this.searchTrips.bind(this)
		this.reverseTrip = this.reverseTrip.bind(this)
		this.isRootUrl = this.isRootUrl.bind(this)
		this.isOutsidePurchasePage = this.isOutsidePurchasePage.bind(this)
		this.redirectToTicketpurchase = this.redirectToTicketpurchase.bind(this)
		this.findLocalityByName = this.findLocalityByName.bind(this)
		this.prepareRoutesToRebooking = this.prepareRoutesToRebooking.bind(this)
		this.prepareRoutesFromCampaignLink = this.prepareRoutesFromCampaignLink.bind(this)
		this.setCities = this.setCities.bind(this)
	}

	componentDidUpdate(prevProps) {
		const updates = {}

		if (prevProps.origin !== this.props.origin) {
			updates.origin = this.props.origin
				? this.props.origin.value
				: JSON.stringify({ id: '' })
		}

		if (prevProps.destination !== this.props.destination) {
			updates.destination = this.props.destination
				? this.props.destination.value
				: JSON.stringify({ id: '' })
		}

		const prevGoingTime = prevProps.tripDateGoingSearch
			? new Date(prevProps.tripDateGoingSearch).getTime()
			: null
		const currGoingTime = this.props.tripDateGoingSearch
			? new Date(this.props.tripDateGoingSearch).getTime()
			: null
		if (prevGoingTime !== currGoingTime) {
			updates.tripDateGoingSearch = this.props.tripDateGoingSearch
				? moment(this.props.tripDateGoingSearch).format('YYYY-MM-DD')
				: null
		}

		const prevReturnTime = prevProps.tripDateReturnSearch
			? new Date(prevProps.tripDateReturnSearch).getTime()
			: null
		const currReturnTime = this.props.tripDateReturnSearch
			? new Date(this.props.tripDateReturnSearch).getTime()
			: null
		if (prevReturnTime !== currReturnTime) {
			updates.tripDateReturnSearch = this.props.tripDateReturnSearch
				? moment(this.props.tripDateReturnSearch).format('YYYY-MM-DD')
				: null
		}

		if (Object.keys(updates).length > 0) {
			this.setState(prev => ({
				formValues: { ...prev.formValues, ...updates }
			}))
		}
	}

	async componentDidMount() {
		this.props.setTripData({
			tripsGoing: [],
			tripsReturn: []
		})

		// 7820: pré-carrega as origens (lista ranqueada pelas mais buscadas) para o dropdown
		// já exibir sugestões ao focar, sem o usuário precisar digitar.
		this.preloadOrigins()

		const tripParams = iFrameHelper.getTripParams()

		const {
			isRebooking,
			rebookingOrigin,
			rebookingDestination,
			rebookingOriginId,
			rebookingDestinationId
		} = this.props.rebookingData

		if (isRebooking) {
			// Remarcação é sempre para a mesma origem/destino do bilhete original - nunca uma escolha
			// manual do usuário. Tenta resolver por id (mais preciso, quando o backend já resolveu
			// contra o catálogo) e cai pra busca por nome só pro lado que não veio com id/não resolveu
			// (mesclado num único caminho pra nunca deixar de resolver um lado só por falta de id).
			this.prepareRoutesToRebooking(
				rebookingOrigin,
				rebookingDestination,
				rebookingOriginId,
				rebookingDestinationId
			)
		}

		const rootURL = document.location.href

		const {
			FACEBOOK_SOURCE_TAG,
			GOOGLE_SOURCE_TAG,
			TIKTOK_SOURCE_TAG
		} = URL_CAMPAIGN_TERMS

		if (
			checkURLTerm(rootURL, FACEBOOK_SOURCE_TAG) ||
			checkURLTerm(rootURL, GOOGLE_SOURCE_TAG) ||
			checkURLTerm(rootURL, TIKTOK_SOURCE_TAG)
		) {
			this.setCampaignParams(tripParams.campaignCode, rootURL)
		}

		if (!tripParams.isValidParameters) {
			const {
				origin,
				destination,
				tripDateGoingSearch,
				tripDateReturnSearch,
				destinationList,
				rebookingData
			} = this.props

			let defaultDateGoingSearch = dateHelper.getTomorrowDate()

			if (tripDateGoingSearch) {
				defaultDateGoingSearch = tripDateGoingSearch
			}

			this.props.setTripDateGoingSearch(defaultDateGoingSearch)

			if (!origin || !destination || !destinationList) {
				return
			}

			const tripDateReturnValues =
				!isNaN(Date.parse(tripDateReturnSearch)) &&
					!rebookingData.isRebooking
					? tripDateReturnSearch
					: null

			this.props.setDestinationList(destinationList)
			this.props.setTripDateReturnSearch(tripDateReturnValues)

			this.setSearchFormField({
				originId: JSON.parse(origin.value).id,
				destinationId: JSON.parse(destination.value).id,
				tripDateGoingSearch: defaultDateGoingSearch,
				tripDateReturnSearch: tripDateReturnValues
			})

			return
		}

		// Bug 8003: o id do link pode não bater com o catálogo/ambiente atual (link de
		// campanha institucional, gerado em outro brand ou catálogo) - resolve por id
		// quando o formato bate, com fallback pra IBGE/nome, em vez de confiar cegamente
		// no id vindo da URL (ver prepareRoutesFromCampaignLink).
		await this.prepareRoutesFromCampaignLink(tripParams)
	}

	resolveCampaign(rootURL, urlParams) {
		const campaignSources = [
			{
				sourceTag: URL_CAMPAIGN_TERMS.FACEBOOK_SOURCE_TAG,
				campaignTag: URL_CAMPAIGN_TERMS.FACEBOOK_CAMPAIGN_TAG,
				fallbackTag: URL_CAMPAIGN_TERMS.FACEBOOK_SOURCE_TAG,
				source: URL_CAMPAIGN_TERMS.FACEBOOK_SOURCE
			},
			{
				sourceTag: URL_CAMPAIGN_TERMS.GOOGLE_SOURCE_TAG,
				campaignTag: URL_CAMPAIGN_TERMS.GOOGLE_CAMPAIGN_TAG,
				fallbackTag: null,
				source: URL_CAMPAIGN_TERMS.GOOGLE_SOURCE
			},
			{
				sourceTag: URL_CAMPAIGN_TERMS.TIKTOK_SOURCE_TAG,
				campaignTag: URL_CAMPAIGN_TERMS.TIKTOK_CAMPAIGN_TAG,
				fallbackTag: URL_CAMPAIGN_TERMS.TIKTOK_SOURCE_TAG,
				source: URL_CAMPAIGN_TERMS.TIKTOK_SOURCE
			}
		]

		for (const {
			sourceTag,
			campaignTag,
			fallbackTag,
			source
		} of campaignSources) {
			if (!checkURLTerm(rootURL, sourceTag)) continue

			const code =
				urlParams.get(campaignTag.toLowerCase()) ||
				(fallbackTag && urlParams.get(fallbackTag.toLowerCase()))

			return { campaignCode: code, sourceCampaign: source }
		}

		return null
	}

	setCampaignParams(code, rootURL) {
		const urlParams = new URLSearchParams(new URL(rootURL).search)

		const resolved = this.resolveCampaign(rootURL, urlParams)

		const campaignCode = resolved
			? resolved.campaignCode
			: code || URL_CAMPAIGN_TERMS.NO_CAMPAIGN_CODE

		const sourceCampaign = resolved
			? resolved.sourceCampaign
			: URL_CAMPAIGN_TERMS.NO_SOURCE_CAMPAIGN

		this.props.setRootURL(rootURL)
		this.props.setCampaignCode(campaignCode)
		this.props.setCampaignSource(sourceCampaign)
	}

	async getOriginList(term) {
		const { data } = await TicketSaleService.getOrigins(term)
		const origins = data ? data.items : []

		if (!origins || !origins.length) return []

		return flattenLocalities(origins)
	}

	// 7820: busca a lista inicial de origens (termo vazio => backend devolve a lista ranqueada
	// pelas mais buscadas) e a guarda no estado para o AsyncSelect exibir sem digitar. Só busca
	// quando ainda não há lista carregada, para não sobrescrever origens de remarcação/URL.
	async preloadOrigins() {
		if (this.props.originList && this.props.originList.length) return

		const originList = await this.getOriginList('')

		if (originList && originList.length) {
			this.props.setOriginList(originList)
		}
	}

	formatLocalityOptionLabel(option, { context }) {
		const isMenu = context === 'menu'
		const isTerminal = isMenu && option.kind === LOCALITY_KIND.TERMINAL
		const isCity = isMenu && option.kind === LOCALITY_KIND.CITY

		return (
			<OriginOptionLabel isTerminal={isTerminal} isCity={isCity}>
				{option.label}
			</OriginOptionLabel>
		)
	}

	async getDestinationList(origin, term) {
		try {
			const originId = JSON.parse(origin.value).id
			const { data } = await TicketSaleService.getDestinations(originId, term)
			const destinationsFound = data ? data.items : []

			const destinations = flattenLocalities(destinationsFound)

			return sortTerminalsByPriority(destinations, PRIORITY_TERM)
		} catch (error) {
			if (!getApiErrorMessage(error)) {
				this.props.toastWarning({
					message: 'Ocorreu um erro ao buscar lista de destinos.'
				})
			}

			return []
		}
	}

	findDestinations(inputValue, callback) {
		if (
			!inputValue ||
			inputValue.toUpperCase() === this.props.searchDestinationTerm
		) {
			callback(this.props.destinationList)
			return
		}

		if (!this.props.origin) {
			callback([])
			return
		}

		debounce(
			1000,
			isCurrent => {
				if (!isCurrent()) return

				const term = inputValue.toUpperCase()

				this.getDestinationList(this.props.origin, term).then(
					destinationListFound => {
						if (!isCurrent()) return

						this.props.setSearchDestinationTerm(term)
						this.props.setDestinationList(destinationListFound)
						callback(destinationListFound)
					}
				)
			},
			DEBOUNCE_FIND_DESTINATIONS
		)
	}

	findOrigins(inputValue, callback) {
		if (
			!inputValue ||
			inputValue.toUpperCase() === this.props.searchOriginTerm
		) {
			callback(this.props.originList)
			return
		}

		debounce(
			1000,
			isCurrent => {
				if (!isCurrent()) return

				this.props.setDestination(null)
				this.props.setDestinationList([])

				this.alterDestinationField(
					JSON.stringify({
						id: ''
					})
				)

				const term = inputValue.toUpperCase()

				this.getOriginList(term).then(originListFound => {
					if (!isCurrent()) return

					this.props.setSearchOriginTerm(term)
					this.props.setOriginList(originListFound)
					callback(originListFound)
				})
			},
			DEBOUNCE_FIND_ORIGINS
		)
	}

	setDestination(destination) {
		if (this.state.presetDestination) {
			this.setState({ presetDestination: null })
		}

		if (!destination) return

		const destinationId = JSON.parse(destination).id

		const selectedDestination = this.props.destinationList.find(
			({ value }) => JSON.parse(value).id === destinationId
		)

		if (selectedDestination) {
			this.props.setDestination(selectedDestination)
		}
	}

	validateLocalities() {
		const { origin, destination, tripDateGoingSearch } = this.props

		if (!origin || !destination || !tripDateGoingSearch) {
			this.props.toastWarning({
				message: 'Escolha origem, destino e data de ida'
			})
			return
		}

		if (origin.label === destination.label) {
			this.props.toastWarning({
				message: 'A origem e o destino devem ser distintos'
			})
			return
		}

		this.props.setEtapaPagamento(false)

		this.searchTrips(this.state.formValues)
	}

	redirectToTicketpurchase() {
		const {
			origin,
			destination,
			tripDateGoingSearch,
			tripDateReturnSearch,
			history,
			isIframe
		} = this.props

		const tripDateReturnValid = tripDateReturnSearch
			? moment(tripDateReturnSearch).format('YYYY/MM/DD')
			: null

		let url = routes.COMPRA_DE_PASSAGEM

		if (document.location.pathname.includes(routes.PLANALTO_SITE) || isIframe) {
			// Bug 8003: além do id de catálogo (atalho, válido só no catálogo/ambiente onde foi
			// gerado), o link carrega origem/destino como "Cidade - UF" - chave estável que
			// qualquer catálogo consegue resolver por nome (ver resolveLocality/findLocalityByName).
			// Empresa cai como string vazia (nunca a palavra "undefined" na URL) quando o catálogo
			// atual não preenche busCompany (fluxo MuviHub não tem esse conceito).
			url +=
				`?IdOrigem=${origin.id}` +
				`&IdDestino=${destination.id}` +
				`&Origem=${origin.city} - ${origin.state || ''}` +
				`&Destino=${destination.city} - ${destination.state || ''}` +
				`&DestinoCity=${destination.city}` +
				`&OriginCity=${origin.city}` +
				`&Empresa=${destination.busCompany || ''}` +
				`&DataIda=${moment(tripDateGoingSearch).format('YYYY/MM/DD')}` +
				`&DataVolta=${tripDateReturnValid}` +
				`&hasChildren=${origin.hasChildren}`
		}

		if (isIframe) {
			window.top.location = url
		} else {
			history.push(url)
		}
	}

	isRootUrl() {
		return document.location.pathname === '/'
	}

	isOutsidePurchasePage() {
		return !document.location.pathname.includes(routes.COMPRA_DE_PASSAGEM)
	}

	async searchTrips(values) {
		const {
			origin,
			destination,
			tripDateGoingSearch,
			tripDateReturnSearch
		} = values

		if (!origin || !destination) return

		const originId = JSON.parse(origin).id
		const destinationId = JSON.parse(destination).id

		if (!originId || !destinationId || !this.props.originList.length) {
			return
		}

		// Shown right away, before the redirect below, so the loader stays
		// visible across the navigation instead of only appearing once
		// performSearchTrips's own showLoading() fires (which happens after
		// the redirect + getPlaces resolve, leaving a gap with no feedback)
		this.props.showLoading()

		try {
			if (this.isRootUrl() || this.isOutsidePurchasePage()) {
				this.redirectToTicketpurchase()
				this.props.resetRebookingData()
			}

			let { origin, destination, destinationList } = await this.getPlaces(
				originId,
				destinationId
			)

			if (!origin || !destination) {
				this.props.toastWarning({
					message: 'Origem ou destino não encontrados.'
				})

				return
			}

			this.props.resetConexaoState()

			await this.props.performSearchTrips({
				origin,
				originId,
				destination,
				destinationId,
				tripDateGoingSearch,
				tripDateReturnSearch,
				originList: this.props.originList
			})

			if (!this.props.origin) {
				this.props.setTripDateReturnSearch(tripDateReturnSearch)
				this.props.setTripDateGoingSearch(tripDateGoingSearch)
				this.props.setDestinationList(destinationList)
				this.props.setOrigin(origin)
				this.props.setDestination(destination)

				this.setSearchFormField({
					originId,
					destinationId,
					tripDateGoingSearch,
					tripDateReturnSearch
				})
			}

		} catch (error) {
			if (!getApiErrorMessage(error)) {
				this.props.toastWarning({
					message: 'Ocorreu um erro ao tentar buscar as viagens'
				})
			}
		} finally {
			this.props.hideLoading()
		}
	}

	async getPlaces(originId, destinationId) {
		const origin = this.props.originList.find(
			origin => JSON.parse(origin.value).id === originId
		)

		if (!origin) return {}

		const currentDestination = this.props.destination
		const currentDestinationIsSelected =
			currentDestination &&
			JSON.parse(currentDestination.value).id === destinationId

		if (currentDestinationIsSelected) {
			return {
				origin,
				destination: currentDestination,
				destinationList: this.props.destinationList
			}
		}

		const destinationList = await this.getDestinationList(origin)

		if (!destinationList) return { origin, destinationList: [] }

		const destination = destinationList.find(
			destination => JSON.parse(destination.value).id === destinationId
		)

		if (!destination) return { origin, destinationList }

		return { origin, destination, destinationList }
	}

	setSearchFormField({
		originId,
		destinationId,
		tripDateGoingSearch,
		tripDateReturnSearch
	}) {
		this.setState(prev => ({
			formValues: {
				...prev.formValues,
				origin: JSON.stringify({ id: originId }),
				destination: JSON.stringify({ id: destinationId }),
				tripDateGoingSearch: tripDateGoingSearch
					? moment(tripDateGoingSearch).format('YYYY-MM-DD')
					: null,
				...(tripDateReturnSearch && {
					tripDateReturnSearch: moment(tripDateReturnSearch).format('YYYY-MM-DD')
				})
			}
		}))
	}

	filterBusByFreeSeats(ticketRoutes) {
		return ticketRoutes
			.filter(route => route.freeSeats > 0)
			.map(markUpgradableTicket)
	}

	setCities({ destination, origin, destinations, origins }) {
		// O AsyncSelectInput só exibe o valor selecionado corretamente se ele já
		// estiver presente na lista de opções (mesmo cuidado de reverseTrip) -
		// por isso as listas (origins/destinations) são aplicadas antes do value,
		// e o preset garante o rótulo certo mesmo antes da lista carregar.
		if (origins) {
			this.props.setOriginList(origins)
		}
		this.props.setDestinationList(destinations)
		this.props.setOrigin(origin)
		this.alterOriginField(origin.value)

		// Em remarcação, o destino pode não ser encontrado automaticamente
		// (ex: nome não bate com nenhuma rota do novo destino) - nesse caso
		// deixamos o campo em branco para o usuário escolher manualmente.
		if (destination) {
			this.props.setDestination(destination)
			this.alterDestinationField(destination.value)
		}

		this.setState({
			presetOrigin: origin,
			presetDestination: destination || null
		})
	}

	async reverseTrip() {
		const {
			origin,
			destination,
			showLoading,
			hideLoading,
			toastWarning
		} = this.props

		if (this.props.rebookingData.isRebooking) {
			toastWarning({
				message: 'Em remarcação não é possível inverter origem e destino.'
			})
			return
		}

		if (!origin || !destination) {
			toastWarning({
				message: 'Escolha origem e destino para poder inverter'
			})
			return
		}

		showLoading()

		try {
			const { data } = await TicketSaleService.swapLocalities({
				currentOriginId: JSON.parse(origin.value).id,
				currentDestinationId: JSON.parse(destination.value).id
			})

			if (!data || !data.origin || !data.destination) {
				toastWarning({
					message: 'Não foi possível inverter origem e destino.'
				})
				return
			}

			const newOrigin = mapLocalityOption(data.origin)
			const newDestination = mapLocalityOption(data.destination)

			// O AsyncSelectInput só exibe o valor selecionado corretamente se
			// ele já estiver presente na lista de opções - por isso as listas
			// (origins/destinations) precisam ser aplicadas antes do value.
			this.props.setOriginList(flattenLocalities(data.origins || []))
			this.props.setDestinationList(flattenLocalities(data.destinations || []))

			this.props.setOrigin(newOrigin)
			this.props.setDestination(newDestination)

			this.setState({
				presetOrigin: newOrigin,
				presetDestination: newDestination
			})

			this.alterOriginField(newOrigin.value)
			this.alterDestinationField(newDestination.value)

		} catch (error) {
			toastWarning({
				message: 'Ocorreu um erro ao tentar inverter origem e destino.'
			})
		} finally {
			hideLoading()
		}
	}

	// A API de pedidos devolve, quando resolvido contra o catálogo do /travel/origins (Spec
	// remarcação-busca-por-id), o id opaco da localidade (loc_city_<guid>/loc_term_<guid>) junto
	// com o nome ("Porto Alegre - RS"). Sem chamada de rede: nem busca por nome, nem precisa a
	// opção já estar em originList/destinationList - a lista vira literalmente esse item único
	// (mesma salvaguarda de sempre pro BaseAsyncSelectField, ver setCities/reverseTrip).
	buildLocalityOptionFromId(id, cityAndState) {
		// Bug 7975: bilhetes RJ/TTBus interestadual e de conexao intermunicipal trazem o id
		// numerico do legado/provedor preenchido em vez do id opaco do catalogo do Hub - aceitar
		// qualquer id "truthy" aqui gera uma opcao com id invalido, e a busca de viagens (que exige
		// o formato loc_city_/loc_term_ + GUID) volta 422. So aceita quando o formato bate; caso
		// contrario cai pro findLocalityByName (resolveRebookingLocality).
		if (!id || !cityAndState || !isValidLocalityId(id)) return null

		const { city, state } = splitCityState(cityAndState)

		return mapLocalityOption({
			id,
			displayName: `${titleCase(city)}, ${state}`,
			stateCode: state,
			kind: LOCALITY_KIND.CITY,
			children: null
		})
	}

	// Resolve uma localidade (origem ou destino): tenta pelo id opaco primeiro (mais preciso, quando
	// o backend/link já resolveu contra o catálogo) e cai pra busca por nome (findLocalityByName)
	// sempre que o id vier ausente OU não bater no formato do catálogo atual (pedido antigo já em
	// cache, link de campanha gerado em outro catálogo/ambiente, cidade sem estação ativa). Usado
	// tanto pela remarcação (prepareRoutesToRebooking) quanto pelo link de campanha
	// (prepareRoutesFromCampaignLink, Bug 8003) - generalizado em vez de duplicar a mesma cadeia
	// id -> nome nos dois fluxos.
	async resolveLocality(id, cityAndState, loader, ibgeCode) {
		const byId = this.buildLocalityOptionFromId(id, cityAndState)
		if (byId) return { locality: byId, list: [byId] }

		return this.findLocalityByName(cityAndState, loader, ibgeCode)
	}

	// Busca por nome ("PORTO ALEGRE - RS" -> cidade "PORTO ALEGRE", UF "RS"). Devolve também a
	// lista usada na busca: o AsyncSelectInput só exibe o valor selecionado corretamente se ele já
	// estiver presente na lista de opções (mesmo cuidado documentado em reverseTrip) - sem isso o
	// campo aparece em branco e a reconciliação do BaseAsyncSelectField chega a limpar o valor de
	// volta pra vazio.
	//
	// Bug real: o legado manda a cidade em CAIXA ALTA ("PORTO ALEGRE"), mas `l.city` (mapLocalityOption)
	// vem do catálogo em case de exibição ("Porto Alegre") - a comparação estrita `l.city === cityName`
	// NUNCA batia, então SEMPRE caía silenciosamente em `localitiesFound[0]` (o primeiro resultado da
	// busca, não necessariamente a cidade certa) - explicava tanto a origem errada quanto o destino
	// "não encontrado" (buscado a partir da origem já errada). Comparação agora ignora
	// maiúsculas/minúsculas e também confere a UF (duas cidades podem ter o mesmo nome em estados
	// diferentes) - mesmo critério do LegacyLocalityResolver (MuviHub).
	//
	// ibgeCode (opcional, Bug 8003): quando o link de campanha traz o código IBGE, ele desempata
	// entre localidades encontradas pelo nome antes do critério cidade+UF - mais confiável que o
	// nome sozinho quando a cidade foi renormalizada ou existe mais de uma vez no catálogo.
	async findLocalityByName(locationName, loader, ibgeCode) {
		const { city: cityName, state: stateCode } = splitCityState(locationName)

		if (!cityName) return { locality: null, list: [] }

		const localitiesFound = await loader(cityName)

		if (!localitiesFound || !localitiesFound.length) return { locality: null, list: [] }

		const normalizedCity = cityName.toUpperCase()
		const normalizedState = (stateCode || '').toUpperCase()

		const locality =
			(ibgeCode && localitiesFound.find(l => String(l.ibgeCode) === String(ibgeCode))) ||
			localitiesFound.find(l =>
				(l.city || '').toUpperCase() === normalizedCity &&
				(!normalizedState || (l.state || '').toUpperCase() === normalizedState)
			) || localitiesFound[0]

		return { locality, list: localitiesFound }
	}

	// Preenche origem/destino da remarcação (Spec remarcação-busca-por-id): sempre a mesma rota do
	// bilhete original, resolvida por id quando disponível e por nome como fallback - nunca deixada
	// em branco pro usuário escolher manualmente (originId/destinationId são undefined pra um
	// pedido legado-only sem o id resolvido; resolveLocality cai pro nome sozinho nesse caso).
	async prepareRoutesToRebooking(originName, destinationName, originId, destinationId) {
		const { locality: newOrigin, list: originsFound } = await this.resolveLocality(
			originId,
			originName,
			term => this.getOriginList(term)
		)

		if (!newOrigin) {
			this.props.toastWarning({
				message: 'Não foi possível carregar a origem da remarcação automaticamente. Tente novamente em instantes.'
			})
			return
		}

		const { locality: newDestination, list: destinationsFound } = await this.resolveLocality(
			destinationId,
			destinationName,
			term => this.getDestinationList(newOrigin, term)
		)

		if (!newDestination) {
			this.props.toastWarning({
				message: 'Não foi possível carregar o destino da remarcação automaticamente. Tente novamente em instantes.'
			})
		}

		this.setCities({
			destination: newDestination,
			destinations: newDestination
				? destinationsFound
				: await this.getDestinationList(newOrigin),
			origin: newOrigin,
			origins: originsFound
		})
	}

	// Preenche origem/destino/data a partir de um link de campanha (institucional, anúncio) - Bug
	// 8003. Mesma cadeia de resolução da remarcação (id opaco do catálogo atual -> IBGE -> nome),
	// generalizada em resolveLocality/findLocalityByName em vez de duplicada aqui: o id de catálogo
	// é um atalho quando o link foi gerado no mesmo catálogo/ambiente, nunca um requisito - por isso
	// nome (+ UF) e IBGE continuam funcionando sozinhos, e o link nunca fica preso a um catálogo
	// específico. Ao contrário da remarcação, aqui a origem/destino é uma sugestão do link, não uma
	// obrigação: se não resolver, o usuário preenche manualmente (com aviso claro, nunca uma tela
	// vazia em silêncio).
	async prepareRoutesFromCampaignLink({
		originId,
		destinationId,
		originName,
		destinationName,
		originIbge,
		destinationIbge,
		tripDateGoingSearch,
		tripDateReturnSearch
	}) {
		this.props.showLoading()

		// O link de campanha traz a data crua da URL (ex.: "2026/09/29"), mas o backend
		// (trips/search) exige departureDate/returnDate em "yyyy-MM-dd". No fluxo manual essa
		// conversão já acontece porque o Formik mantém a data nesse formato (ver
		// setSearchFormField/constructor); aqui precisa ser feita explicitamente antes de
		// propagar a data pro Redux (setTripDateGoingSearch/setTripDateReturnSearch) e pra busca
		// (performSearchTrips), senão a API rejeita com "departureDate must be in yyyy-MM-dd format".
		tripDateGoingSearch = tripDateGoingSearch
			? moment(tripDateGoingSearch).format('YYYY-MM-DD')
			: tripDateGoingSearch
		tripDateReturnSearch = tripDateReturnSearch
			? moment(tripDateReturnSearch).format('YYYY-MM-DD')
			: tripDateReturnSearch

		try {
			const { locality: origin, list: originsFound } = await this.resolveLocality(
				originId,
				originName,
				term => this.getOriginList(term),
				originIbge
			)

			if (!origin) {
				this.props.toastWarning({
					message: 'Não foi possível localizar a origem do link. Selecione a origem manualmente.'
				})
				return
			}

			const { locality: destination, list: destinationsFound } = await this.resolveLocality(
				destinationId,
				destinationName,
				term => this.getDestinationList(origin, term),
				destinationIbge
			)

			this.setCities({
				destination,
				destinations: destination
					? destinationsFound
					: await this.getDestinationList(origin),
				origin,
				origins: originsFound
			})

			if (!destination) {
				this.props.toastWarning({
					message: 'Não foi possível localizar o destino do link. Selecione o destino manualmente.'
				})
				return
			}

			this.props.setTripDateGoingSearch(tripDateGoingSearch)
			this.props.setTripDateReturnSearch(tripDateReturnSearch)

			this.setSearchFormField({
				originId: origin.id,
				destinationId: destination.id,
				tripDateGoingSearch,
				tripDateReturnSearch
			})

			this.props.resetConexaoState()

			try {
				await this.props.performSearchTrips({
					origin,
					originId: origin.id,
					destination,
					destinationId: destination.id,
					tripDateGoingSearch,
					tripDateReturnSearch,
					originList: originsFound
				})
			} catch (error) {
				if (!getApiErrorMessage(error)) {
					this.props.toastWarning({
						message: 'Ocorreu um erro ao tentar buscar as viagens'
					})
				}
			}
		} finally {
			this.props.hideLoading()
		}
	}

	async updateDestinations(newOriginLocality) {
		if (this.state.presetOrigin) {
			this.setState({ presetOrigin: null })
		}

		if (!newOriginLocality) {
			return
		}

		let newOriginId

		try {
			newOriginId = JSON.parse(newOriginLocality).id
		} catch (error) {
			return
		}

		const currentOriginId = this.props.origin
			? JSON.parse(this.props.origin.value).id
			: null

		if (newOriginId === currentOriginId) {
			return
		}

		const selectedOrigin = this.props.originList.find(({ value }) => {
			try {
				return JSON.parse(value).id === newOriginId
			} catch (error) {
				return false
			}
		})

		if (!selectedOrigin) {
			return
		}

		const destinationListReadyToShow = await this.getDestinationList(
			selectedOrigin
		)

		if (!destinationListReadyToShow || !destinationListReadyToShow.length)
			return

		const currentDestination = this.props.destination
		const currentDestinationIsValid =
			currentDestination &&
			destinationListReadyToShow.some(
				dest =>
					JSON.parse(dest.value).id === JSON.parse(currentDestination.value).id
			)

		if (!currentDestinationIsValid) {
			this.alterDestinationField(
				JSON.stringify({
					id: ''
				})
			)
		}

		this.props.setOrigin(selectedOrigin)
		this.props.setDestinationList(destinationListReadyToShow)
	}

	alterOriginField(value) {
		this.setState(prev => ({
			formValues: { ...prev.formValues, origin: value }
		}))
	}

	alterDestinationField(value) {
		this.setState(prev => ({
			formValues: { ...prev.formValues, destination: value }
		}))
	}

	renderFormik(props) {
		const isRootUrlState = this.isRootUrl()
		const isMobileState = isMobile()
		const { isIframe } = this.props

		return (
			<Form noValidate>
				<SearchContainer
					isrooturl={isRootUrlState}
					isIframe={isIframe}
				>
					<InputSearchCitiesContainer isrooturl={isRootUrlState} isIframe={isIframe}>
						<InputSearchCity>
							<AsyncSelectInput
								{...props}
								id="origin"
								placeholder={'Origem'}
								defaultOptions={this.props.originList}
								defaultValue={this.state.presetOrigin}
								loadOptions={this.findOrigins}
								onValueChange={this.updateDestinations}
								disabled={this.props.rebookingData.isRebooking}
								formatOptionLabel={this.formatLocalityOptionLabel}
							/>
						</InputSearchCity>
						<StyledIconFontAwesome
							name="exchange"
							title="Inverter destino"
							onClick={this.reverseTrip}
							id="ico-reverse"
							isrooturl={isRootUrlState}
							isIframe={isIframe}
						/>
						<InputSearchCity>
							<AsyncSelectInput
								{...props}
								id="destination"
								placeholder="Destino"
								defaultOptions={this.props.destinationList}
								defaultValue={this.state.presetDestination}
								loadOptions={this.findDestinations}
								onValueChange={this.setDestination}
								disabled={this.props.rebookingData.isRebooking}
								formatOptionLabel={this.formatLocalityOptionLabel}
							/>
						</InputSearchCity>
					</InputSearchCitiesContainer>
					<InputSearchDateContainer isIframe={isIframe}>
						<InputSearchDate>
							<DatepickerInput
								{...props}
								id="tripDateGoingSearch"
								placeholder="Data da ida"
								isOutsideRange={value => {
									const tripDateReturnSearch = this.props
										.tripDateReturnSearch
									return (
										value.isBefore(moment().startOf('day')) ||
										value.isAfter(
											moment()
												.startOf('day')
												.add(ALLOWED_RANGE_IN_MONTHS, 'M')
										) ||
										(tripDateReturnSearch &&
											value.isAfter(
												moment(
													tripDateReturnSearch,
													'YYYY-MM-DD'
												).add(1, 'days')
											))
									)
								}}
								onValueChange={this.props.setTripDateGoingSearch}
							/>
						</InputSearchDate>
						<InputSearchDate>
							<DatepickerInput
								{...props}
								showClearDate
								id="tripDateReturnSearch"
								placeholder="Data da volta (opcional)"
								isOutsideRange={value => {
									const tripDateGoingSearch = this.props
										.tripDateGoingSearch
									return (
										value.isBefore(moment().startOf('day')) ||
										(tripDateGoingSearch &&
											value.isBefore(
												moment(tripDateGoingSearch, 'YYYY-MM-DD')
											))
									)
								}}
								onValueChange={this.props.setTripDateReturnSearch}
								disabled={this.props.rebookingData.isRebooking}
							/>
						</InputSearchDate>
					</InputSearchDateContainer>
					<BtnSearchTrip isrooturl={isRootUrlState.toString()} isIframe={isIframe}>
						<Button
							isIframe={isIframe}
							primary={isRootUrlState.toString()}
							secondary={(!isRootUrlState).toString()}
							type="button"
							disabled={this.props.loading}
							onClick={this.validateLocalities}
							height={isMobileState ? null : '4.5rem'}>
							Buscar
						</Button>
					</BtnSearchTrip>
				</SearchContainer>
			</Form>
		)
	}

	render() {
		return (
			<Formik
				enableReinitialize
				initialValues={this.state.formValues}
				validationSchema={searchFormSchema}
				render={this.renderFormik}
				onSubmit={this.searchTrips}
			/>
		)
	}
}

const mapStateToProps = store => {
	return {
		user: store.user,
		loading: store.site.loading,
		origin: store.ticketSale.origin,
		originList: store.ticketSale.originList,
		searchOriginTerm: store.ticketSale.searchOriginTerm,
		destination: store.ticketSale.destination,
		destinationList: store.ticketSale.destinationList,
		searchDestinationTerm: store.ticketSale.searchDestinationTerm,
		tripDateGoingSearch: store.ticketSale.tripDateGoingSearch,
		tripDateReturnSearch: store.ticketSale.tripDateReturnSearch,
		rebookingData: store.ticketSale.rebookingData,
		campaignCode: store.ticketSale.campaignCode
	}
}

const mapDispatchToProps = dispatch =>
	bindActionCreators(
		{
			toastWarning: toastActions.warning,
			hideLoading: SiteCreators.hideLoading,
			showLoading: SiteCreators.showLoading,
			setOriginPortal: TicketSaleCreators.setOriginPortal,
			resetConexaoState: conexaoActions.resetConexaoState,
			setTripData: TicketSaleCreators.setTripData,
			setOrigin: TicketSaleCreators.setOrigin,
			setOriginList: TicketSaleCreators.setOriginList,
			setSearchOriginTerm: TicketSaleCreators.setSearchOriginTerm,
			setDestination: TicketSaleCreators.setDestination,
			setDestinationList: TicketSaleCreators.setDestinationList,
			setSearchDestinationTerm: TicketSaleCreators.setSearchDestinationTerm,
			setTripDateGoingSearch: TicketSaleCreators.setTripDateGoingSearch,
			setTripDateReturnSearch: TicketSaleCreators.setTripDateReturnSearch,
			setEtapaPagamento: TicketSaleCreators.setEtapaPagamento,
			resetRebookingData: TicketSaleCreators.resetRebookingData,
			setCampaignCode: TicketSaleCreators.setCampaignCode,
			setRootURL: TicketSaleCreators.setRootURL,
			setCampaignSource: TicketSaleCreators.setCampaignSource,
			performSearchTrips: TicketSaleCreators.performSearchTrips
		},
		dispatch
	)

export const SearchForm = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(SearchFormComponent)
)

export default SearchForm
